import { create } from "zustand";
import { supabase } from "./supabase";
import { Track } from "@/types/track";
import { ilikeContainsPattern, isRankedSearch } from "@/lib/search-ranking";

export type ViewMode = "grid" | "list";

export interface ArtistView {
  id: string;
  name: string;
  coverUrl: string | null;
  avatarUrl: string | null;
  fallbackAvatarUrl?: string;
  tracks: Track[];
  genres: string[];
  totalTracks: number;
  totalAlbums: number;
  earliestRelease: string;
  latestRelease: string;
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  isVirtual: true;
  artistName: string;
}

const PAGE_SIZE = 50;

interface ArtistFilters<Q> {
  eq(column: "label_tag", value: string): Q;
  in(column: "label_tag", values: readonly string[]): Q;
  ilike(column: "name", pattern: string): Q;
}

/**
 * Which artists match: label scoping + the name search. Shared by the sorted list query and the
 * ranked path's count, so the count always describes the same rows as the grid. Published-only
 * needs no filter here: v_artists_with_counts lists only artists with a published track linked in
 * the artist's own label (step 1b), so a draft-only artist never reaches this page.
 */
function filterArtists<Q extends ArtistFilters<Q>>(
  query: Q,
  labelTag: string | null | undefined,
  filterLabelTags: string[],
  term: string
): Q {
  // Label scoping: seller's label takes precedence, then buyer's filter
  if (labelTag) {
    query = query.eq("label_tag", labelTag);
  } else if (filterLabelTags.length > 0) {
    query = query.in("label_tag", filterLabelTags);
  }

  // Server-side search by artist name
  if (term) {
    query = query.ilike("name", ilikeContainsPattern(term));
  }
  return query;
}

/**
 * The query behind the artists on screen. A reset load commits it in the same `set` as its rows, so
 * whatever describes those rows reads this, not `searchQuery`: the keystrokes run ahead of the
 * debounced reload, and the rows answer the search that was applied.
 */
interface AppliedArtistsQuery {
  /**
   * The reset load that committed these rows. A load-more continues this query only while this is
   * still the store's `_generation`: any newer reset (search, sort, label filter or label change, or
   * `reset()`) retires it, so a page can never be appended into a different list.
   */
  generation: number;
  /** The trimmed search term; "" when the rows are not a search. */
  search: string;
  /** "Best match": ordered by search_artists_with_counts instead of the sort. */
  ranked: boolean;
  sortBy: ArtistsState["sortBy"];
  sortOrder: ArtistsState["sortOrder"];
}

interface ArtistsState {
  /** Wipe artist data + counts + selection + filters (logout/account switch). Keeps view/sort prefs. */
  reset: () => void;
  artists: ArtistView[];
  /** Exact count of the artists the applied query matches. Counted on a reset only; a load-more keeps it. */
  totalCount: number;
  hasMore: boolean;
  pageIndex: number;
  isFetchingMore: boolean;
  lastLabelTag: string | null;
  isSelectionMode: boolean;
  selectedArtistIds: Set<string>;
  viewMode: ViewMode;
  isLoading: boolean;
  isEnrichingImages: boolean;
  error: string | null;
  searchQuery: string;
  /** What the artists on screen were loaded with. `null` until the first load commits, and after `reset()`. */
  appliedQuery: AppliedArtistsQuery | null;
  sortBy: "name" | "tracks" | "albums" | "recent" | "oldest";
  sortOrder: "asc" | "desc";
  /**
   * The user picked a sort during the current search (the one on screen or the one being typed), so
   * that search is not ranked. Cleared in the same `set` that commits artists loaded with no search:
   * the next search starts on "Best match" again.
   */
  sortPickedDuringSearch: boolean;
  filterLabelTags: string[];
  availableLabels: Array<{ id: string; name: string }>;
  loadArtists: (
    labelTag?: string | null,
    page?: number,
    pageSize?: number,
    reset?: boolean
  ) => Promise<void>;
  fetchLabels: () => Promise<void>;
  enrichWithSpotifyImages: () => Promise<void>;
  toggleSelectionMode: () => void;
  toggleArtistSelection: (id: string) => void;
  selectAllArtists: () => void;
  clearSelection: () => void;
  setViewMode: (mode: ViewMode) => void;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: ArtistsState["sortBy"]) => void;
  setSortOrder: (order: ArtistsState["sortOrder"]) => void;
  /** `"relevance"` returns the active search to "Best match"; it is never stored as `sortBy`. */
  setSort: (
    sortBy: ArtistsState["sortBy"] | "relevance",
    sortOrder: ArtistsState["sortOrder"]
  ) => void;
  setFilterLabelTags: (tags: string[]) => void;
  updateArtistImage: (artistId: string, imageUrl: string | null) => void;
  _generation: number;
}

function generateArtistAvatar(artistName: string): string {
  const encodedName = encodeURIComponent(artistName);
  return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodedName}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf&scale=80`;
}

export const useArtistsStore = create<ArtistsState>((set, get) => ({
  artists: [],
  totalCount: 0,
  hasMore: true,
  pageIndex: 0,
  isFetchingMore: false,
  lastLabelTag: null,
  isSelectionMode: false,
  selectedArtistIds: new Set(),
  viewMode: "grid",
  isLoading: false,
  isEnrichingImages: false,
  error: null,
  searchQuery: "",
  appliedQuery: null,
  sortBy: "recent",
  sortOrder: "desc",
  sortPickedDuringSearch: false,
  filterLabelTags: [],
  availableLabels: [],
  _generation: 0,

  reset: () =>
    set((state) => ({
      artists: [],
      totalCount: 0,
      hasMore: true,
      pageIndex: 0,
      isFetchingMore: false,
      lastLabelTag: null,
      isSelectionMode: false,
      selectedArtistIds: new Set<string>(),
      isLoading: false,
      isEnrichingImages: false,
      error: null,
      searchQuery: "",
      appliedQuery: null,
      sortPickedDuringSearch: false,
      filterLabelTags: [],
      availableLabels: [],
      _generation: state._generation + 1,
    })),

  loadArtists: async (labelTag, _page, pageSize = PAGE_SIZE, reset = true) => {
    const { filterLabelTags } = get();
    let query: AppliedArtistsQuery;

    if (reset) {
      const { searchQuery, sortBy, sortOrder, sortPickedDuringSearch, _generation } = get();
      const search = searchQuery.trim();
      query = {
        generation: _generation + 1,
        search,
        ranked: isRankedSearch(search !== "", sortPickedDuringSearch),
        sortBy,
        sortOrder,
      };
      set({ isLoading: true, error: null, pageIndex: 0, _generation: query.generation });
    } else {
      // A load-more continues the artists on screen, never the search still being typed. Before the
      // first commit, or once a newer reset is under way, there is nothing to continue.
      const { appliedQuery, isFetchingMore, _generation } = get();
      if (isFetchingMore || !appliedQuery || appliedQuery.generation !== _generation) return;
      query = appliedQuery;
      set({ isFetchingMore: true, error: null });
    }
    const gen = query.generation;

    try {
      const pageIndex = reset ? 0 : get().pageIndex;
      const from = pageIndex * pageSize;
      const to = from + pageSize - 1;

      // Query the view which pre-computes track_count, album_count, genres. The ranked function
      // returns rows of the same view.
      // The exact count walks every matching artist, so only a reset (the first page of a new
      // query) asks for it; a load-more continues the same query and keeps the total it counted.
      let artistRows: unknown[] | null;
      let count: number | null;

      if (query.ranked) {
        // "Best match": the database orders by how the name matches the term (exact > starts
        // with > a word starts with > contains). A function's result carries no total, so the
        // count is its own query over the same filters: a GET limited to one row, not a HEAD
        // request, which drew Supabase gateway 503s (see TracksPage fetchCounts).
        const [rankedResult, countResult] = await Promise.all([
          supabase.rpc("search_artists_with_counts", {
            p_search: query.search,
            p_label_tags: labelTag
              ? [labelTag]
              : filterLabelTags.length > 0
                ? filterLabelTags
                : undefined,
            p_limit: pageSize,
            p_offset: from,
          }),
          reset
            ? filterArtists(
                supabase
                  .from("v_artists_with_counts")
                  .select("id", { count: "exact" })
                  .limit(1),
                labelTag,
                filterLabelTags,
                query.search
              )
            : null,
        ]);
        if (rankedResult.error) throw rankedResult.error;
        if (countResult?.error) throw countResult.error;
        artistRows = rankedResult.data;
        count = countResult?.count ?? null;
      } else {
        // Map sortBy to database column for server-side ordering
        let orderColumn: string;
        let ascending: boolean;
        switch (query.sortBy) {
          case "name":
            orderColumn = "name";
            ascending = query.sortOrder === "asc";
            break;
          case "tracks":
            orderColumn = "track_count";
            ascending = query.sortOrder === "asc";
            break;
          case "albums":
            orderColumn = "album_count";
            ascending = query.sortOrder === "asc";
            break;
          case "oldest":
            orderColumn = "created_at";
            ascending = query.sortOrder === "asc";
            break;
          case "recent":
          default:
            orderColumn = "updated_at";
            ascending = query.sortOrder === "asc";
            break;
        }

        const view = supabase.from("v_artists_with_counts");
        const sortedResult = await filterArtists(
          (reset ? view.select("*", { count: "exact" }) : view.select("*"))
            .order(orderColumn, { ascending })
            .range(from, to),
          labelTag,
          filterLabelTags,
          query.search
        );
        if (sortedResult.error) throw sortedResult.error;
        artistRows = sortedResult.data;
        count = sortedResult.count;
      }

      const newArtists: ArtistView[] = (artistRows || []).map((a: any) => {
        const profileImage =
          typeof a.profile_image_url === "string" &&
          a.profile_image_url.trim().length > 0
            ? a.profile_image_url
            : null;
        const coverUrl = profileImage || generateArtistAvatar(a.name);

        return {
          id: a.id as string,
          name: a.name,
          coverUrl,
          avatarUrl: profileImage || generateArtistAvatar(a.name),
          fallbackAvatarUrl: profileImage
            ? generateArtistAvatar(a.name)
            : undefined,
          tracks: [],
          genres: a.genres || [],
          totalTracks: a.track_count ?? 0,
          totalAlbums: a.album_count ?? 0,
          earliestRelease: a.created_at || "",
          latestRelease: a.updated_at || a.created_at || "",
          createdAt: a.created_at || "",
          updatedAt: a.updated_at || "",
          isPublic: true,
          isVirtual: true as const,
          artistName: a.name,
        };
      });

      const hasMore = (artistRows || []).length === pageSize;

      // Discard stale results if a newer reset has started
      if (get()._generation !== gen) return;

      set((state) => {
        const combined = reset
          ? newArtists
          : [...state.artists, ...newArtists];
        const deduped = Array.from(
          new Map(combined.map((a) => [a.id, a])).values()
        );
        const committed = {
          artists: deduped,
          lastLabelTag: labelTag ?? null,
          isLoading: false,
          isFetchingMore: false,
          isEnrichingImages: false,
          hasMore,
          pageIndex: hasMore ? pageIndex + 1 : pageIndex,
        };
        // A load-more asked for no count: the total stays the one its reset counted, but never
        // below the rows on screen (a reset that got no count fell back to its own rows).
        if (!reset) return { ...committed, totalCount: Math.max(state.totalCount, deduped.length) };
        // New rows and the query they answer land together: nothing can read one without the other.
        return {
          ...committed,
          totalCount: count ?? deduped.length,
          appliedQuery: query,
          // Artists loaded with no search end the search: the next one starts on "Best match" again.
          sortPickedDuringSearch: query.search === "" ? false : state.sortPickedDuringSearch,
        };
      });
    } catch (error: any) {
      if (get()._generation !== gen) return;
      console.error("Failed to load artists:", error);
      set({
        error: error?.message || "Failed to load artists. Please try again.",
        isLoading: false,
        isFetchingMore: false,
      });
    }
  },

  fetchLabels: async () => {
    try {
      const { data, error } = await supabase
        .from("labels")
        .select("id, name")
        .order("name", { ascending: true });
      if (error) throw error;
      set({ availableLabels: data || [] });
    } catch (error) {
      console.error("Failed to fetch labels:", error);
    }
  },

  enrichWithSpotifyImages: async () => {
    // No-op placeholder; can re-enable enrichment if needed
  },

  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedArtistIds: new Set(),
    })),
  toggleArtistSelection: (id) =>
    set((state) => {
      const next = new Set(state.selectedArtistIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { selectedArtistIds: next };
    }),
  selectAllArtists: () =>
    set((state) => ({
      selectedArtistIds: new Set(state.artists.map((a) => a.id)),
    })),
  clearSelection: () => set({ selectedArtistIds: new Set() }),
  setViewMode: (mode) => set({ viewMode: mode }),
  setSearchQuery: (query) => {
    set({ searchQuery: query });
    // Search is server-side — page component handles debounce + re-fetch
  },

  setSortBy: (sortBy) => {
    // Use setSort for combined updates to avoid double re-fetch
    set({ sortBy });
  },

  setSortOrder: (sortOrder) => {
    // Use setSort for combined updates to avoid double re-fetch
    set({ sortOrder });
  },

  setSort: (sortBy, sortOrder) => {
    if (sortBy === "relevance") {
      set({ sortPickedDuringSearch: false });
    } else {
      const { searchQuery, appliedQuery } = get();
      set({
        sortBy,
        sortOrder,
        // A pick during a search: the one this reload applies, or the one still on screen while a
        // cleared search's reload is pending (its rows, and the menu, still show that search).
        sortPickedDuringSearch: searchQuery.trim() !== "" || Boolean(appliedQuery?.search),
      });
    }
    const { lastLabelTag } = get();
    get().loadArtists(lastLabelTag, undefined, PAGE_SIZE, true);
  },

  setFilterLabelTags: (tags) => {
    set({ filterLabelTags: tags });
    // A reset: the new label filter's first page, re-counted
    const { lastLabelTag } = get();
    get().loadArtists(lastLabelTag, undefined, PAGE_SIZE, true);
  },

  updateArtistImage: (artistId, imageUrl) => {
    set((state) => ({
      artists: state.artists.map((a) =>
        a.id === artistId
          ? { ...a, avatarUrl: imageUrl, coverUrl: imageUrl }
          : a
      ),
    }));
  },
}));

// Export hook alias for legacy imports
export const useArtists = useArtistsStore;
